import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Download, Activity, Upload, Loader2, SlidersHorizontal, Rocket, Boxes, Trash2, Plus, CalendarClock, MonitorSmartphone, X, FilterX, Printer, Rows3 } from "lucide-react";
import Header from "@/components/Header";
import StatsBar from "@/components/StatsBar";
import RolloutBar from "@/components/RolloutBar";
import LabelSheet from "@/components/LabelSheet";
import DeviceTable from "@/components/DeviceTable";
import DeviceDrawer from "@/components/DeviceDrawer";
import { useLang } from "@/i18n";
import { exportDevicesCsv } from "@/lib/export";
import { tvStatusOf } from "@/lib/fieldPrefs";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

export default function Dashboard() {
  const { t, lang } = useLang();
  const de = lang === "de";
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ total: 0, online: 0, tv_reachable: 0, offline: 0, no_agent: 0 });
  const [autoSync, setAutoSync] = useState(false);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mfgFilter, setMfgFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [tvFilter, setTvFilter] = useState("all");
  const [lifecycleFilter, setLifecycleFilter] = useState("all");
  const today = new Date().toISOString().slice(0, 10);
  const isClosed = (d) => !!(d.closes_on && d.closes_on <= today);
  const isClosingSoon = (d) => !!(d.closes_on && d.closes_on > today);
  const [checkedCodes, setCheckedCodes] = useState(() => new Set());
  const toggleSelect = (code) =>
    setCheckedCodes((prev) => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const toggleAll = (codes, on) =>
    setCheckedCodes((prev) => { const n = new Set(prev); codes.forEach((c) => (on ? n.add(c) : n.delete(c))); return n; });
  const clearSel = () => setCheckedCodes(new Set());
  const [labelOpen, setLabelOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showRollout, setShowRollout] = useState(() => localStorage.getItem("fleet_show_rollout") === "1");
  const [dense, setDense] = useState(() => localStorage.getItem("fleet_dense") === "1");
  const toggleDense = () => setDense((v) => { const n = !v; localStorage.setItem("fleet_dense", n ? "1" : "0"); return n; });
  const toggleRollout = () => {
    setShowRollout((v) => {
      const n = !v;
      localStorage.setItem("fleet_show_rollout", n ? "1" : "0");
      return n;
    });
  };
  const ALL_FILTERS = [
    ["agent", t("agent_filter")],
    ["status", t("filter_status")],
    ["tv", "TV"],
    ["lifecycle", de ? "Schließung" : "Lifecycle"],
    ["group", de ? "Gruppe" : "Group"],
    ["state", t("bundesland")],
    ["city", t("ort")],
    ["manufacturer", t("filter_manufacturer")],
  ];
  const [fMenu, setFMenu] = useState(false);
  const [mFilterOpen, setMFilterOpen] = useState(false);
  const [visFilters, setVisFilters] = useState(() => {
    try {
      const s = localStorage.getItem("fleet_visible_filters");
      return new Set(s ? JSON.parse(s) : ALL_FILTERS.map((f) => f[0]));
    } catch {
      return new Set(ALL_FILTERS.map((f) => f[0]));
    }
  });
  const toggleFilter = (k) => {
    setVisFilters((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      localStorage.setItem("fleet_visible_filters", JSON.stringify([...next]));
      return next;
    });
  };
  const fOn = (k) => visFilters.has(k);

  const filtersActive = statusFilter !== "all" || mfgFilter !== "all" || agentFilter !== "all" || stateFilter !== "all" || cityFilter !== "all" || groupFilter !== "all" || tvFilter !== "all" || lifecycleFilter !== "all";
  const resetFilters = () => {
    setStatusFilter("all"); setMfgFilter("all"); setAgentFilter("all"); setStateFilter("all");
    setCityFilter("all"); setGroupFilter("all"); setTvFilter("all"); setLifecycleFilter("all");
  };

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([api.get("/devices"), api.get("/stats")]);
      setDevices(d.data);
      setStats(s.data);
      setSelected((prev) => (prev ? d.data.find((x) => x.device_code === prev.device_code) || prev : prev));
    } catch (err) {
      // interceptor handles auth
    }
  }, []);

  const assignGroup = async () => {
    const g = window.prompt(de ? "Gruppenname (leer = Gruppe entfernen):" : "Group name (empty = clear group):", "");
    if (g === null) return;
    try {
      const res = await api.post("/devices/bulk/group", { device_codes: [...checkedCodes], group: g });
      toast.success(`${res.data.updated} ${de ? "Geräte aktualisiert" : "devices updated"}`);
      clearSel();
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const deleteSelected = async () => {
    if (!window.confirm(`${checkedCodes.size} ${de ? "Geräte löschen?" : "delete devices?"}`)) return;
    try {
      const res = await api.post("/devices/bulk/delete", { device_codes: [...checkedCodes] });
      toast.success(`${res.data.deleted} ${de ? "gelöscht" : "deleted"}`);
      clearSel();
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const bulkClose = async () => {
    const d = window.prompt(de ? "Schließdatum (JJJJ-MM-TT), leer = entfernen:" : "Closing date (YYYY-MM-DD), empty = clear:", "");
    if (d === null) return;
    try {
      const res = await api.post("/devices/bulk/close", { device_codes: [...checkedCodes], closes_on: d });
      toast.success(`${res.data.updated} ${de ? "Stationen aktualisiert" : "stations updated"}`);
      clearSel();
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const [tvRegistering, setTvRegistering] = useState(false);
  const registerTv = async () => {
    if (tvRegistering) return;
    if (!window.confirm(de ? "Alle Stationen mit TV-ID im TeamViewer-Konto registrieren, damit der Live-Status verfügbar wird?" : "Register all stations with a TV-ID in the TeamViewer account to enable live status?")) return;
    setTvRegistering(true);
    try {
      await api.post("/teamviewer/register-all", {});
      toast.info(de ? "Registrierung läuft…" : "Registering…");
      const started = Date.now();
      const poll = async () => {
        try {
          const r = await api.get("/teamviewer/register-status");
          if (r.data && r.data.running === false && r.data.finished_at) {
            setTvRegistering(false);
            toast.success(`${de ? "Fertig" : "Done"}: +${r.data.added || 0} ${de ? "neu" : "new"}, ${r.data.skipped || 0} ${de ? "vorhanden" : "existing"}, ${r.data.failed || 0} ${de ? "fehlgeschlagen" : "failed"}`);
            load();
            return;
          }
        } catch (e) { /* ignore */ }
        if (Date.now() - started > 120000) { setTvRegistering(false); load(); return; }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (err) { setTvRegistering(false); toast.error(apiError(err)); }
  };

  const newStation = async () => {
    const code = window.prompt(de ? "Geräte-Code der neuen Station (z. B. FRAT03-01):" : "Device code (e.g. FRAT03-01):", "");
    if (!code) return;
    const name = window.prompt(de ? "Standort-Name (optional):" : "Location name (optional):", "") || "";
    try {
      const res = await api.post("/devices/create", { device_code: code.trim(), location_name: name });
      toast.success(`${res.data.device_code} ${de ? "angelegt" : "created"}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const importSheet = async () => {    const url = window.prompt(t("import_url_prompt"), "https://docs.google.com/spreadsheets/d/1r07sjGTtSF4xNxqDEHA_eyg0oAt0JIVXeH5ewJPFq3w/edit?usp=sharing");
    if (!url) return;
    setImporting(true);
    try {
      const res = await api.post("/import/sheet", { url });
      toast.success(`${res.data.imported} ${t("import_done")}`);
      setAutoSync(true);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImporting(false);
    }
  };

  // initial load + auto-sync status
  useEffect(() => {
    load();
    api.get("/sync").then((r) => setAutoSync(!!(r.data && r.data.enabled))).catch(() => {});
  }, [load]);

  // Tools-Dropdown (Header) -> Export/Import; Remote-Dropdown -> TV registrieren
  useEffect(() => {
    const onExport = () => { exportDevicesCsv(filtered); toast.success(lang === "de" ? "CSV exportiert" : "CSV exported"); };
    const onImport = () => importSheet();
    const onTvReg = () => registerTv();
    window.addEventListener("tools:export", onExport);
    window.addEventListener("tools:import", onImport);
    window.addEventListener("remote:tv-register", onTvReg);
    return () => {
      window.removeEventListener("tools:export", onExport);
      window.removeEventListener("tools:import", onImport);
      window.removeEventListener("remote:tv-register", onTvReg);
    };
  });

  // live push via WebSocket, with slow polling fallback (debounced to avoid counter jitter)
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = base.replace(/^http/, "ws") + "/api/ws";
    let ws;
    let closed = false;
    let retry;
    let debTimer = null;
    const scheduleLoad = () => {
      if (debTimer) return; // coalesce bursts of heartbeats into one reload
      debTimer = setTimeout(() => { debTimer = null; load(); }, 2500);
    };
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        ws.onmessage = () => scheduleLoad();
        ws.onclose = () => {
          if (!closed) retry = setTimeout(connect, 4000);
        };
        ws.onerror = () => ws && ws.close();
      } catch (e) {
        retry = setTimeout(connect, 4000);
      }
    };
    connect();
    const iv = setInterval(load, 20000); // fallback
    return () => {
      closed = true;
      clearTimeout(retry);
      clearTimeout(debTimer);
      clearInterval(iv);
      ws && ws.close();
    };
  }, [load]);

  const manufacturers = useMemo(() => Array.from(new Set(devices.map((d) => d.manufacturer).filter(Boolean))).sort(), [devices]);
  const states = useMemo(() => Array.from(new Set(devices.map((d) => d.extra && d.extra["Bundesland"]).filter(Boolean))).sort(), [devices]);
  const cities = useMemo(() => Array.from(new Set(devices.map((d) => d.extra && d.extra["Ort"]).filter(Boolean))).sort(), [devices]);
  const groups = useMemo(() => Array.from(new Set(devices.map((d) => d.group).filter(Boolean))).sort(), [devices]);

  const counts = useMemo(() => {
    const c = { status: { online: 0, tv_reachable: 0, offline: 0, no_agent: 0 }, agent: { installed: 0, missing: 0 }, state: {}, city: {}, mfg: {}, group: {}, tv: { online: 0, offline: 0, attention: 0, unknown: 0, none: 0 }, lifecycle: { active: 0, closed: 0, soon: 0 } };
    for (const d of devices) {
      if (d.status && c.status[d.status] !== undefined) c.status[d.status]++;
      if (d.agent_installed) c.agent.installed++; else c.agent.missing++;
      if (isClosed(d)) c.lifecycle.closed++; else if (isClosingSoon(d)) c.lifecycle.soon++; else c.lifecycle.active++;
      c.tv[tvStatusOf(d)]++;
      const st = (d.extra || {})["Bundesland"]; if (st) c.state[st] = (c.state[st] || 0) + 1;
      const ci = (d.extra || {})["Ort"]; if (ci) c.city[ci] = (c.city[ci] || 0) + 1;
      const mf = d.manufacturer; if (mf) c.mfg[mf] = (c.mfg[mf] || 0) + 1;
      const gr = d.group; if (gr) c.group[gr] = (c.group[gr] || 0) + 1;
    }
    return c;
  }, [devices]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = devices.filter((d) => {
      if (statusFilter !== "all" && d.status !== statusFilter) return false;
      if (mfgFilter !== "all" && d.manufacturer !== mfgFilter) return false;
      if (agentFilter === "installed" && !d.agent_installed) return false;
      if (agentFilter === "missing" && d.agent_installed) return false;
      if (stateFilter !== "all" && (d.extra || {})["Bundesland"] !== stateFilter) return false;
      if (cityFilter !== "all" && (d.extra || {})["Ort"] !== cityFilter) return false;
      if (groupFilter !== "all" && d.group !== groupFilter) return false;
      const tvS = tvStatusOf(d);
      if (tvFilter === "online" && tvS !== "online") return false;
      if (tvFilter === "offline" && tvS !== "offline") return false;
      if (tvFilter === "attention" && tvS !== "attention") return false;
      if (tvFilter === "unknown" && tvS !== "unknown") return false;
      if (tvFilter === "none" && tvS !== "none") return false;
      if (lifecycleFilter === "closed" && !isClosed(d)) return false;
      if (lifecycleFilter === "soon" && !isClosingSoon(d)) return false;
      if (lifecycleFilter === "active" && (isClosed(d) || isClosingSoon(d))) return false;
      return true;
    });
    if (!q) return base;
    // 1) Kennung zuerst: asset_tag / device_code / location_id
    const idMatch = base.filter(
      (d) => String(d.asset_tag || "").toLowerCase().includes(q) || String(d.device_code || "").toLowerCase().includes(q) || String(d.location_id || "").toLowerCase().includes(q)
    );
    if (idMatch.length) return idMatch;
    // 2) Fallback: breite Suche über alle Felder
    return base.filter((d) =>
      [d.asset_tag, d.hostname, d.device_code, d.tablet_serial, d.device_uid, d.teamviewer_id, d.model, d.manufacturer, d.location_name, d.location_id]
        .concat(Object.values(d.extra || {}))
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [devices, query, statusFilter, mfgFilter, agentFilter, stateFilter, cityFilter, groupFilter, tvFilter, lifecycleFilter]);

  // Barcode-Scan im Suchfeld: Enter öffnet direkt den passenden Datensatz
  const openByScan = () => {
    const raw = query.trim();
    if (!raw) return;
    const ql = raw.toLowerCase();
    const exact = devices.find((d) => String(d.asset_tag || "").toLowerCase() === ql || String(d.device_code || "").toLowerCase() === ql);
    const match = exact || (filtered.length === 1 ? filtered[0] : null);
    if (match) setSelected(match);
    else navigate(`/d/${encodeURIComponent(raw)}`);  // z. B. Lagergerät -> Scan-Seite löst per Asset-Tag auf
  };

  const selCls = "w-full lg:w-auto appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-2.5 text-sm text-gray-700 focus:border-[#c00000] outline-none cursor-pointer";

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F8FAFC] text-gray-900">
      <Header />
      <div className="shrink-0 w-full px-4 sm:px-6 pt-4 pb-3 space-y-4 border-b border-gray-200">
        <StatsBar stats={stats} active={statusFilter} onSelect={setStatusFilter} />
        {showRollout && <RolloutBar stats={stats} autoSync={autoSync} />}

        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
            <input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); openByScan(); } }}
              placeholder={t("search_placeholder")}
              className="w-full bg-white border border-gray-200 rounded-lg pl-10 pr-10 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-1 focus:ring-[#c00000] outline-none transition-colors"
            />
            {query && (
              <button
                data-testid="search-clear-btn"
                onClick={() => setQuery("")}
                title={lang === "de" ? "Suche löschen" : "Clear search"}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative shrink-0">
              <button
                data-testid="filter-config-btn"
                onClick={() => setFMenu((v) => !v)}
                title={t("columns")}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 hover:border-gray-300 transition-colors"
              >
                <SlidersHorizontal className="w-4 h-4" />
              </button>
              {fMenu && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setFMenu(false)} />
                  <div data-testid="filter-config-menu" className="absolute left-0 top-12 z-30 w-56 bg-white border border-gray-200 rounded-xl shadow-2xl p-2">
                    {ALL_FILTERS.map(([k, label]) => (
                      <label key={k} data-testid={`filter-toggle-${k}`} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-100 cursor-pointer text-sm">
                        <input type="checkbox" checked={fOn(k)} onChange={() => toggleFilter(k)} className="accent-[#c00000] w-4 h-4" />
                        <span className="text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              data-testid="filter-reset-btn"
              onClick={resetFilters}
              disabled={!filtersActive}
              title={lang === "de" ? "Filter zurücksetzen" : "Reset filters"}
              className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm border transition-colors shrink-0 ${
                filtersActive
                  ? "bg-amber-500/15 border-amber-500/30 text-amber-700 hover:bg-amber-500/25"
                  : "bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed"
              }`}
            >
              <FilterX className="w-4 h-4" />
            </button>
            <button
              data-testid="toggle-rollout"
              onClick={toggleRollout}
              title={t("rollout_title")}
              className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm border transition-colors shrink-0 ${
                showRollout
                  ? "bg-[#c00000]/15 border-[#c00000]/30 text-[#c00000]"
                  : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
              }`}
            >
              <Rocket className="w-4 h-4" />
            </button>
            <button
              data-testid="toggle-density"
              onClick={toggleDense}
              title={de ? (dense ? "Komfortable Zeilen" : "Kompakte Zeilen") : (dense ? "Comfortable rows" : "Compact rows")}
              className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm border transition-colors shrink-0 ${
                dense
                  ? "bg-[#c00000]/15 border-[#c00000]/30 text-[#c00000]"
                  : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
              }`}
            >
              <Rows3 className="w-4 h-4" />
            </button>
            <button
              data-testid="mobile-filter-toggle"
              onClick={() => setMFilterOpen((v) => !v)}
              className={`lg:hidden flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm border transition-colors shrink-0 ${
                mFilterOpen || filtersActive
                  ? "bg-[#c00000]/15 border-[#c00000]/30 text-[#c00000]"
                  : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
              }`}
            >
              <Filter className="w-4 h-4" /> {de ? "Filter" : "Filters"}{filtersActive ? " •" : ""}
            </button>
            <div className={`${mFilterOpen ? "grid grid-cols-1" : "hidden"} lg:flex lg:flex-row lg:flex-wrap gap-2 w-full lg:w-auto`}>
            {fOn("agent") && (
            <select data-testid="filter-agent" value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)} className={selCls}>
              <option value="all">{t("agent_filter")}: {t("all")} ({devices.length})</option>
              <option value="installed">{t("agent_installed_f")} ({counts.agent.installed})</option>
              <option value="missing">{t("agent_missing_f")} ({counts.agent.missing})</option>
            </select>
            )}
            {fOn("status") && (
            <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selCls}>
              <option value="all">{t("filter_status")}: {t("all")} ({devices.length})</option>
              <option value="online">{t("online")} ({counts.status.online})</option>
              <option value="tv_reachable">{t("tv_reachable")} ({counts.status.tv_reachable})</option>
              <option value="offline">{t("offline")} ({counts.status.offline})</option>
              <option value="no_agent">{t("no_agent")} ({counts.status.no_agent})</option>
            </select>
            )}
            {fOn("group") && (
            <select data-testid="filter-group" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className={selCls}>
              <option value="all">{de ? "Gruppe" : "Group"}: {t("all")}</option>
              {groups.map((m) => <option key={m} value={m}>{m} ({counts.group[m] || 0})</option>)}
            </select>
            )}
            {fOn("tv") && (
            <select data-testid="filter-tv" value={tvFilter} onChange={(e) => setTvFilter(e.target.value)} className={selCls}>
              <option value="all">TV: {t("all")}</option>
              <option value="online">TV Online ({counts.tv.online})</option>
              <option value="offline">TV Offline ({counts.tv.offline})</option>
              <option value="attention">{de ? "TV nicht im Konto" : "TV not in account"} ({counts.tv.attention})</option>
              <option value="unknown">{de ? "TV Unbekannt" : "TV Unknown"} ({counts.tv.unknown})</option>
              <option value="none">{de ? "TV Ohne ID" : "No TV ID"} ({counts.tv.none})</option>
            </select>
            )}
            {fOn("lifecycle") && (
            <select data-testid="filter-lifecycle" value={lifecycleFilter} onChange={(e) => setLifecycleFilter(e.target.value)} className={selCls}>
              <option value="all">{de ? "Schließung" : "Lifecycle"}: {t("all")}</option>
              <option value="active">{de ? "Aktiv" : "Active"} ({counts.lifecycle.active})</option>
              <option value="soon">{de ? "Schließt bald" : "Closing soon"} ({counts.lifecycle.soon})</option>
              <option value="closed">closed ({counts.lifecycle.closed})</option>
            </select>
            )}
            {fOn("state") && (
            <select data-testid="filter-state" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className={selCls}>
              <option value="all">{t("bundesland")}: {t("all")}</option>
              {states.map((m) => <option key={m} value={m}>{m} ({counts.state[m] || 0})</option>)}
            </select>
            )}
            {fOn("city") && (
            <select data-testid="filter-city" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className={selCls}>
              <option value="all">{t("ort")}: {t("all")}</option>
              {cities.map((m) => <option key={m} value={m}>{m} ({counts.city[m] || 0})</option>)}
            </select>
            )}
            {fOn("manufacturer") && (
            <select data-testid="filter-manufacturer" value={mfgFilter} onChange={(e) => setMfgFilter(e.target.value)} className={selCls}>
              <option value="all">{t("filter_manufacturer")}: {t("all")}</option>
              {manufacturers.map((m) => <option key={m} value={m}>{m} ({counts.mfg[m] || 0})</option>)}
            </select>
            )}
            </div>
            <button
              data-testid="new-station"
              onClick={newStation}
              className="flex items-center gap-2 bg-gray-100 border border-gray-200 text-gray-700 rounded-lg px-3 py-2.5 text-sm hover:bg-gray-200 transition-colors whitespace-nowrap shrink-0"
            >
              <Plus className="w-4 h-4" /> {de ? "Neue Station" : "New station"}
            </button>
            <button
              data-testid="open-activity"
              onClick={() => navigate("/activity")}
              className="flex items-center gap-2 bg-[#c00000]/15 border border-[#c00000]/30 text-[#c00000] rounded-lg px-3 py-2.5 text-sm hover:bg-[#c00000]/25 transition-colors whitespace-nowrap shrink-0"
            >
              <Activity className="w-4 h-4" /> {t("activity")}
            </button>
          </div>
        </div>
        {checkedCodes.size > 0 && (
          <div data-testid="bulk-bar" className="flex items-center gap-2 flex-wrap bg-white shadow-sm rounded-lg px-4 py-2.5 border border-[#c00000]/30">
            <span className="text-sm text-[#c00000] font-mono mr-1">{checkedCodes.size} {de ? "ausgewählt" : "selected"}</span>
            <button
              data-testid="bulk-assign-group"
              onClick={assignGroup}
              className="flex items-center gap-2 bg-[#c00000]/15 border border-[#c00000]/30 text-[#c00000] rounded-lg px-3 py-1.5 text-sm hover:bg-[#c00000]/25 transition-colors"
            >
              <Boxes className="w-4 h-4" /> {de ? "Gruppe zuweisen" : "Assign group"}
            </button>
            <button
              data-testid="bulk-close"
              onClick={bulkClose}
              className="flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 text-amber-700 rounded-lg px-3 py-1.5 text-sm hover:bg-amber-500/25 transition-colors"
            >
              <CalendarClock className="w-4 h-4" /> {de ? "Schließdatum" : "Closing date"}
            </button>
            <button
              data-testid="bulk-labels"
              onClick={() => setLabelOpen(true)}
              className="flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-3 py-1.5 text-sm hover:bg-gray-100 transition-colors"
            >
              <Printer className="w-4 h-4" /> {de ? "Etiketten drucken" : "Print labels"}
            </button>
            <button
              data-testid="bulk-delete"
              onClick={deleteSelected}
              className="flex items-center gap-2 text-red-600 border border-red-500/30 bg-red-500/5 rounded-lg px-3 py-1.5 text-sm hover:bg-red-500/15 transition-colors"
            >
              <Trash2 className="w-4 h-4" /> {de ? "Löschen" : "Delete"}
            </button>
            <button
              data-testid="bulk-clear"
              onClick={clearSel}
              className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {de ? "Auswahl aufheben" : "Clear"}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-auto w-full px-4 sm:px-6 pb-6">
        <DeviceTable
          devices={filtered}
          onOpen={setSelected}
          onChanged={load}
          lang={lang}
          dense={dense}
          selectedCodes={checkedCodes}
          onToggleSelect={toggleSelect}
          onToggleAll={toggleAll}
        />
      </div>

      {selected && (
        <DeviceDrawer device={selected} onClose={() => setSelected(null)} onChanged={load} lang={lang} />
      )}
      {labelOpen && (
        <LabelSheet devices={filtered.filter((d) => checkedCodes.has(d.device_code))} onClose={() => setLabelOpen(false)} de={de} />
      )}
    </div>
  );
}
